import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ROLE_LABEL } from '@/lib/labels';
import api from '@/lib/api';
import { toast } from 'sonner';
import { User as UserIcon, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export default function ProfilePage() {
  const { user, company, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  const initials = ((user?.name || '').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('') || 'U').toUpperCase();

  const saveProfile = async () => {
    setSaving(true);
    try { await api.patch(`/users/${user.id}`, { name }); toast.success('Perfil atualizado'); refreshMe(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwd.next.length < 6) return toast.error('Nova senha mínima de 6 caracteres');
    if (pwd.next !== pwd.confirm) return toast.error('As senhas não conferem');
    setPwdSaving(true);
    try {
      await api.patch(`/users/${user.id}/change-password`, { currentPassword: pwd.current, newPassword: pwd.next });
      toast.success('Senha alterada');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setPwdSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><UserIcon className="w-6 h-6 text-blue-600" /> Meu Perfil</h1>
        <p className="text-sm text-slate-500">Suas informações pessoais e segurança.</p>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="flex items-center gap-4 mb-5">
          <Avatar className="w-16 h-16">
            <AvatarFallback className="bg-blue-600 text-white text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="text-lg font-semibold text-slate-900">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
            <div className="text-xs text-slate-500 mt-1">{(user?.roles || []).map(r => ROLE_LABEL[r]).join(', ')} {company && `• ${company.name}`}</div>
          </div>
        </div>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>E-mail</Label><Input value={user?.email} disabled /></div>
        </div>
        <div className="mt-4">
          <Button onClick={saveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar alterações'}</Button>
        </div>
      </Card>

      <Card className="border-slate-200 p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Alterar senha</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Senha atual</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} /></div>
          <div><Label>Nova senha</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} /></div>
          <div><Label>Confirmar</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} /></div>
        </div>
        <div className="mt-4">
          <Button onClick={changePassword} disabled={pwdSaving} className="bg-blue-600 hover:bg-blue-700">{pwdSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Alterando</> : 'Alterar senha'}</Button>
        </div>
      </Card>
    </div>
  );
}
